/* cspell:ignore nics */
import { getNicDeviceType, isNicConfigurationComplete, isNicVendorDeviceMatch } from '@/components-biz/NicConfiguration/utils';
import type { NicConfigurationItem, NicConfigurationValue } from '@/types/nic-configuration';

import type { ResourceType } from '../../platform/pipeline-kernel/types';
import { pickPricingResourceMeta } from '../../utils/pricingResourceMeta';
import type { VendorDeviceMeta } from '../vendorDevice';

/** 按网卡类型保存的厂商设备计价选型。 */
export type NicPricingByDeviceType = Record<string, VendorDeviceMeta>;

/** 网卡计价资源基础信息。 */
export const NIC_RESOURCE: ResourceType = {
  requirement: '',
  code: 'NIC',
  description: '网卡',
  unit: 'Card',
};

/** 将网卡类型转换为可安全用于 Arco 点路径的字段 key。 */
export function getNicPricingFieldKey(deviceType: string): string {
  return encodeURIComponent(deviceType.trim());
}

/** 生成指定网卡类型的计价字段路径。 */
export function getNicPricingFieldPath(deviceType: string): string {
  return `network.nicPricing.${getNicPricingFieldKey(deviceType)}`;
}

/** 从按类型计价映射中读取指定网卡类型的厂商设备。 */
export function getNicPricingMeta(raw: unknown, deviceType: string): VendorDeviceMeta | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  return (raw as NicPricingByDeviceType)[getNicPricingFieldKey(deviceType)];
}

function getNicPricingEntries(raw: unknown): [string, VendorDeviceMeta][] {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return [];
  return Object.entries(raw as NicPricingByDeviceType).flatMap(([key, meta]) => {
    if (!meta || typeof meta !== 'object' || Array.isArray(meta)) return [];
    let decodedKey = key;
    try {
      decodedKey = decodeURIComponent(key);
    } catch {
      // 非法历史 key 保持原值，避免预置回填中断。
    }
    return [[meta.deviceType || decodedKey, meta]];
  });
}

/** 仅保留与各网卡类型当前计价选型匹配的网卡配置。 */
export function getPricedNics(nics: NicConfigurationValue | undefined, rawPricing: unknown): NicConfigurationValue {
  if (!nics?.length) return [];
  const pricingEntries = getNicPricingEntries(rawPricing);
  if (!pricingEntries.length) return nics;
  return nics.filter((nic) => {
    const deviceType = getNicDeviceType(nic).toLowerCase();
    const pricing = pricingEntries.find(([type]) => type.toLowerCase() === deviceType)?.[1];
    return pricing ? isNicVendorDeviceMatch(nic, pricing) : false;
  });
}

/** 每个已选网卡类型都必须存在匹配厂商设备且已勾选网段的配置。 */
export function isNicPricingComplete(nics: NicConfigurationValue | undefined, rawPricing: unknown): boolean {
  const pricingEntries = getNicPricingEntries(rawPricing);
  if (!pricingEntries.length) return false;
  const pricedNics = getPricedNics(nics, rawPricing);
  return pricingEntries.every(([deviceType, pricing]) =>
    pricedNics.some(
      (nic) =>
        getNicDeviceType(nic).toLowerCase() === deviceType.toLowerCase() &&
        isNicVendorDeviceMatch(nic, pricing) &&
        isNicConfigurationComplete([nic]),
    ),
  );
}

/** 获取单张网卡已选的 IPv4 网段并去重。 */
export function getNicIpv4CidrBlocks(nic: NicConfigurationItem): string[] {
  return [
    ...new Set(
      Object.values(nic.resource || {})
        .flatMap((item) => item.cidrBlocks || [])
        .filter((cidr) => !cidr.includes(':')),
    ),
  ];
}

/** 按“网卡类型 + 厂商设备”生成计价资源，数量为已选 IPv4 网段数。 */
export function buildPricedNicResourceConfigs(nics: NicConfigurationValue | undefined, rawPricing: unknown): ResourceType[] {
  const subnetCountByDeviceType = new Map<string, number>();
  getPricedNics(nics, rawPricing).forEach((nic) => {
    const deviceType = getNicDeviceType(nic);
    if (!deviceType) return;
    subnetCountByDeviceType.set(deviceType, (subnetCountByDeviceType.get(deviceType) || 0) + getNicIpv4CidrBlocks(nic).length);
  });

  return [...subnetCountByDeviceType.entries()].flatMap(([deviceType, subnetCount]) => {
    if (!subnetCount) return [];
    return [
      {
        ...NIC_RESOURCE,
        description: `${deviceType} ${NIC_RESOURCE.description}`,
        ...pickPricingResourceMeta(getNicPricingMeta(rawPricing, deviceType), String(subnetCount)),
        deviceType,
      },
    ];
  });
}
